import { getTwoDigitYearFuture, getTwoDigitYearPast, validateYearMonthDay } from "../date-utils/date-utils.js";
import { IntlMessageFormat } from "intl-messageformat";
function getDateTimeFormattersForLocale(locale) {
  return {
    short: new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "numeric",
      year: "numeric"
    }).format,
    medium: new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short"
    }).format,
    long: new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
      year: "numeric"
    }).format,
    full: new Intl.DateTimeFormat(locale, {
      weekday: "short",
      day: "numeric",
      month: "short"
    }).format,
    "full-long": new Intl.DateTimeFormat(locale, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric"
    }).format,
    "full-long-medium": new Intl.DateTimeFormat(locale, {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric"
    }).format,
    "long-date-time": new Intl.DateTimeFormat(locale, {
      weekday: "short",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    }).format,
    "iso-date": (d) => d.toISOString().split("T")[0],
    "month-medium": new Intl.DateTimeFormat(locale, {
      month: "short"
    }).format,
    "month-full": new Intl.DateTimeFormat(locale, {
      month: "long"
    }).format,
    "month-year": new Intl.DateTimeFormat(locale, {
      month: "long",
      year: "numeric"
    }).format,
    legal: new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "long",
      year: "numeric"
    }).format,
    "day-of-week-short": new Intl.DateTimeFormat(locale, {
      weekday: "narrow"
    }).format,
    "day-of-week-medium": new Intl.DateTimeFormat(locale, {
      weekday: "short"
    }).format,
    "day-of-week-full": new Intl.DateTimeFormat(locale, {
      weekday: "long"
    }).format,
    "time-short": new Intl.DateTimeFormat(locale, {
      timeStyle: "short"
    }).format
  };
}
function getNumberFormattersForLocale(locale, fractionDigits) {
  const defaultNumberOptions = { style: "decimal" };
  const fractionDigitsOptions = { minimumFractionDigits: 2 };
  const numberOptions = fractionDigits ? { ...defaultNumberOptions, ...fractionDigitsOptions } : defaultNumberOptions;
  return {
    //@ts-expect-error typescript lib types for this are incorrect
    default: new Intl.NumberFormat(locale, numberOptions).format
  };
}
function getCurrencyFormattersForLocale(locale, currencyCode) {
  return {
    rounded: new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      roundingMode: "ceil"
    }).format,
    default: new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode
    }).format
  };
}
function normalizeLocale(locale) {
  return locale.toLocaleLowerCase().trim().replace(/_/g, "-");
}
function identifyShortDateFormat(inputLocale, options) {
  const currentLocale = normalizeLocale(inputLocale);
  const tempDateString = new Intl.DateTimeFormat(currentLocale, {
    day: "2-digit",
    year: options.yearFormat === "four-digit" ? "numeric" : "2-digit",
    month: "2-digit",
    timeZone: "UTC"
  }).format(new Date(Date.UTC(3333, 10, 22)));
  const matches = tempDateString.match(/[.\-/]/);
  const dateSeparator = matches ? matches[0] : "/";
  const dateLocaleString = tempDateString.replace(/1/g, "m").replace(/2/g, "d").replace(/3/g, "y").replace(/\s/g, "");
  const dateLocaleArray = dateLocaleString.split(dateSeparator);
  return {
    dateLocaleString,
    dateLocaleArray,
    dateSeparator
  };
}
function parseShortDate(text, options) {
  const normalLocale = normalizeLocale(options.locale);
  const zone = options.timezone ?? "utc";
  const normalText = text.replace(/\s/g, "").trim();
  const matches = /^(\d+)[\.\-/](\d+)[\.\-/](\d+)$/.exec(normalText);
  if (!matches) return /* @__PURE__ */ new Date(NaN);
  const first = parseInt(matches[1], 10);
  const second = parseInt(matches[2], 10);
  const third = parseInt(matches[3], 10);
  if (isNaN(first) || isNaN(second) || isNaN(third)) return /* @__PURE__ */ new Date(NaN);
  const [firstElement, secondElement, thirdElement] = identifyShortDateFormat(normalLocale, {
    yearFormat: "two-digit"
  }).dateLocaleArray;
  if (firstElement === "mm" && secondElement == "dd" && thirdElement === "yy") {
    switch (options.yearFormat) {
      case "two-digit": {
        const year = options.yearType === "past" ? getTwoDigitYearPast(third, options.baseDate) : getTwoDigitYearFuture(third, options.baseDate);
        return getValidatedDate(year, first, second, zone === "utc");
      }
      default:
        return getValidatedDate(third, first, second, zone === "utc");
    }
  }
  if (firstElement === "yy" && secondElement === "mm" && thirdElement === "dd") {
    switch (options.yearFormat) {
      case "two-digit": {
        const year = options.yearType === "past" ? getTwoDigitYearPast(first, options.baseDate) : getTwoDigitYearFuture(first, options.baseDate);
        return getValidatedDate(year, second, third, zone === "utc");
      }
      default:
        return getValidatedDate(first, second, third, zone === "utc");
    }
  }
  switch (options.yearFormat) {
    case "two-digit": {
      const year = options.yearType === "past" ? getTwoDigitYearPast(third, options.baseDate) : getTwoDigitYearFuture(third, options.baseDate);
      return getValidatedDate(year, second, first, zone === "utc");
    }
    default:
      return getValidatedDate(third, second, first, zone === "utc");
  }
}
function getValidatedDate(year, month, day, isUTC = false) {
  return validateYearMonthDay(year, month, day).isValid ? isUTC ? new Date(Date.UTC(year, month - 1, day)) : new Date(year, month - 1, day) : /* @__PURE__ */ new Date(NaN);
}
const localeDateParts = {
  cs: "dmr",
  da: "dm\xE5",
  de: "TMJ",
  en: "dmy",
  es: "dmy",
  fi: "pkv",
  fr: "jma",
  it: "gma",
  nb: "dm\xE5",
  nl: "dmj",
  pl: "dmr",
  pt: "dma",
  sv: "dm\xE5",
  tr: "agy",
  zh: "dmy"
};
const countryToCurrency = {
  cz: "CZK",
  dk: "DKK",
  de: "EUR",
  us: "USD",
  ca: "CAD",
  gb: "GBP",
  es: "EUR",
  fi: "EUR",
  fr: "EUR",
  it: "EUR",
  no: "NOK",
  nl: "EUR",
  pl: "PLN",
  pt: "EUR",
  se: "SEK",
  tr: "TRY",
  cn: "CNY",
  in: "INR"
};
const getCurrencyFromLocale = (locale) => {
  const currencyCountryToCurrency = normalizeLocale(locale).split("-")[1];
  return countryToCurrency[currencyCountryToCurrency] ?? "EUR";
};
function getLocaleCurrencyKey(locale, currency) {
  return `${locale}-${currency}`;
}
function getShortFormatPlaceholder(inputLocale, options) {
  const currentLocale = !inputLocale || inputLocale === "en" ? "en-gb" : normalizeLocale(inputLocale);
  const exampleDate = identifyShortDateFormat(currentLocale, options).dateLocaleString;
  const [language, _] = currentLocale.split("-");
  const abbreviations = localeDateParts[language] ?? localeDateParts.en;
  return exampleDate.replace(/m/g, abbreviations[1]).replace(/d/g, abbreviations[0]).replace(/y/g, abbreviations[2]).replace(/\s/g, "");
}
class Localization {
  constructor(locale = "en-gb", localizations) {
    this.localizations = {};
    this.currencyFormatters = {};
    this.setLocale(locale);
    this.setLocalizations(localizations);
    this.bind();
  }
  bind() {
    this.setLocale = this.setLocale.bind(this);
    this.setLocalizations = this.setLocalizations.bind(this);
    this.formatDateTime = this.formatDateTime.bind(this);
    this.formatDateString = this.formatDateString.bind(this);
    this.formatText = this.formatText.bind(this);
    this.formatNumber = this.formatNumber.bind(this);
    this.formatCurrency = this.formatCurrency.bind(this);
    this.getWeekData = this.getWeekData.bind(this);
    this.getDatePlaceholder = this.getDatePlaceholder.bind(this);
    this.getLocaleDateFormat = this.getLocaleDateFormat.bind(this);
    this.parseLocalDateString = this.parseLocalDateString.bind(this);
  }
  setLocale(locale) {
    this.locale = normalizeLocale(locale);
    this.dateTimeFormatters = getDateTimeFormattersForLocale(this.locale);
  }
  setLocalizations(localizations = {}) {
    this.localizations = Object.fromEntries(
      Object.entries(localizations).map(([key, value]) => [
        key,
        new IntlMessageFormat(value, this.locale)
      ])
    );
  }
  formatDateTime(date, options) {
    if (!date || isNaN(date.getTime())) return options?.invalidText ?? "";
    const formatter = this.dateTimeFormatters[options?.dateTimeFormat ?? "short"];
    if (!formatter) {
      throw new Error(`invalid dateTimeFormat passed to formatDateTime: ${options?.dateTimeFormat}`);
    }
    return formatter(date);
  }
  formatDateString(dateString, options) {
    if (!dateString) return null;
    const date = typeof dateString === "string" ? this.parseLocalDateString(dateString, { dateFormat: "iso" }) : dateString;
    return this.formatDateTime(date, options);
  }
  formatText(key, ...args) {
    const argsValue = args.length < 2 && typeof args[0] !== "string" && typeof args[0] !== "number" ? args[0] : args;
    if (this.localizations) {
      const value = this.localizations[key];
      return value ? value.format(argsValue) : `[${key}]`;
    }
    return `[${key}]`;
  }
  formatNumber(number, options) {
    const format = options?.numberFormat ?? "default";
    const fractionDigits = options?.zeros ?? false;
    const formatters = getNumberFormattersForLocale(this.locale, fractionDigits);
    const formatter = formatters[format];
    if (!formatter) {
      throw new Error(`invalid numberFormat passed to formatNumber: ${options?.numberFormat}`);
    }
    return formatter(number);
  }
  formatCurrency(number, options) {
    const format = options?.currencyFormat ?? "default";
    const code = options?.currencyCode ?? getCurrencyFromLocale(this.locale);
    const key = getLocaleCurrencyKey(this.locale, code);
    let formatters = this.currencyFormatters[key];
    if (!formatters) {
      formatters = getCurrencyFormattersForLocale(this.locale, code);
      this.currencyFormatters[key] = formatters;
    }
    const formatter = formatters[format];
    if (!formatter) {
      throw new Error(`invalid currencyFormat passed to formatCurrency: ${options?.currencyFormat}`);
    }
    return formatter(number);
  }
  // formatDuration
  // Waiting for Intl.durationFormat to be released
  getWeekData() {
    return {
      firstDay: [/en-(us|ca)/, /fr-ca/, /ja/, /ko/, /th/].some((m) => m.test(this.locale)) ? "sun" : "mon"
    };
  }
  getDatePlaceholder(options) {
    switch (options.dateFormat) {
      case "iso": {
        const [language, _] = this.locale.split("-");
        const parts = localeDateParts[language];
        return `${parts[2].repeat(4)}-${parts[1].repeat(2)}-${parts[0].repeat(2)}`;
      }
      case "short": {
        return getShortFormatPlaceholder(this.locale, { yearFormat: "four-digit" });
      }
      case "extra-short": {
        return getShortFormatPlaceholder(this.locale, { yearFormat: "two-digit" });
      }
      default: {
        throw new Error(
          `invalid or unsupported dateFormat passed to getDatePlaceholder: ${options.dateFormat}`
        );
      }
    }
  }
  getLocaleDateFormat(locale, options) {
    const theLocale = locale ?? this.locale;
    return identifyShortDateFormat(theLocale, options || { yearFormat: "four-digit" });
  }
  parseLocalDateString(dateString, options) {
    switch (options.dateFormat) {
      case "iso": {
        const split = dateString.split("-").map((x) => parseInt(x, 10));
        return new Date(split[0], split[1] - 1, split[2]);
      }
      case "short": {
        return parseShortDate(dateString, { locale: this.locale ?? "en-gb", timezone: options.timezone });
      }
      case "extra-short": {
        return parseShortDate(dateString, {
          locale: this.locale ?? "en-gb",
          timezone: options.timezone,
          yearFormat: "two-digit",
          yearType: options.yearType,
          baseDate: options.baseDate
        });
      }
      default: {
        throw new Error(
          `invalid or unsupported dateFormat passed to formatDate: ${options.dateFormat}`
        );
      }
    }
  }
}
export {
  Localization,
  countryToCurrency,
  getCurrencyFormattersForLocale,
  getCurrencyFromLocale,
  getDateTimeFormattersForLocale,
  getNumberFormattersForLocale,
  getShortFormatPlaceholder,
  identifyShortDateFormat,
  normalizeLocale
};
